/** @jsx jsx */

import React, { useState } from "react";

import moment from "moment";

import { jsx, SerializedStyles } from "@emotion/core";
import { Typography, DatePicker, Icon } from "antd";
import { FieldProps, Field, ErrorMessage } from "formik";

import { formHelperStyles } from "./FormHelperStyles";

import { formHelperLabels as LABELS } from "./FormHelperLabels";
import calendarIcon from "../../assets/calendar.svg";
import calendarSelectedIcon from "../../assets/calendar-open.svg";

const { Text } = Typography;

interface Props {
  name: string;

  /**
   *  by default "name" is used to indicate the field name in UI
   *  but if a custom label is to be shown this overrides "name"
   */
  label?: string;

  mandatory?: boolean;

  disabled?: boolean;

  format: string;

  /**
   * props.placeholder is given highest priority
   * fallback: label or name
   */
  placeholder?: string;

  hideError?: boolean;

  customError?: string;

  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;

  className?: string;

  /**
   * the keyboard focus goes to this element as soon as it is rendered.
   * Used it only for 1'st input.
   */
  autoFocus?: boolean;

  /**
   * these props are not necessary for most cases!
   * in a case where a field needs a custom onChange,onBlur use this.
   * NOTE: providing this function will override the functions provided by formik.
   *       the custom functions wil have to update formik state on it's own
   */
  onChange?: (date: moment.Moment | null, dateString: string) => void;
  onBlur?: (e: React.SyntheticEvent) => void;
}

/**
 *
 * @param {Props} props
 * @returns a reusable, customizable DatePicker component to be used only within context of formik.
 */
const FormDatePicker: React.FC<Props> = (props: Props) => {
  const {
    name,
    format,
    customError,
    mandatory,
    label,
    hideError,
    customStyles,
    autoFocus,
    className,
    disabled
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const onDatePickerChange = (open: boolean) => setIsOpen(open);

  //props.placeholder is given highest priority
  // fallback: label or name
  const placeholder = props.placeholder || label || name;

  return (
    <div className={className} css={[formHelperStyles, customStyles]}>
      {label && (
        <label className="d-block">
          {label}
          <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
        </label>
      )}
      <Field name={name}>
        {({ field, form }: FieldProps) => {
          const onChange =
            props.onChange ||
            ((_, dateString) => form.setFieldValue(name, dateString));
          const onBlur = props.onBlur || (() => form.setFieldTouched(name));
          return (
            <DatePicker
              name={field.name}
              open={isOpen}
              onOpenChange={onDatePickerChange}
              //To avoid invalid date, if value is null
              value={field.value ? moment(field.value, format) : null}
              className="date-picker"
              format={format}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={placeholder}
              autoFocus={autoFocus}
              disabled={disabled}
              // Disable Current and later dates.
              disabledDate={dateValue =>
                !dateValue ||
                dateValue?.isSameOrAfter(moment().format("YYYY-MM-DD"))
              }
              showToday={false}
              suffixIcon={
                <Icon
                  component={() => (
                    <img
                      src={isOpen ? calendarSelectedIcon : calendarIcon}
                      alt="Calendar"
                    />
                  )}
                />
              }
            />
          );
        }}
      </Field>
      {!hideError && (
        <ErrorMessage name={name}>
          {msg => (
            <Typography className="error">{customError || msg}</Typography>
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default FormDatePicker;
